import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChange,
  OnDestroy,
  Type,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';
import { XVIError } from '@app/shared/models/xvi-error.model';
import {
  BehaviorSubject,
  Observable,
  observableCombineLatest,
  observableOf,
  rx,
  Subject,
  Subscription,
} from '@app/shared/rx/rx-wrapper';
import { EntityFormServiceProvider } from '@app/shared/services/datasource/entity-form-service-provider.service';
import { StateService } from '@app/shared/services/state-service/state.service';
import {
  AnyObject,
  IBaseEntityType,
  ICommonFormAction,
  IFormGroupPreferences,
  IWidgetAction,
  IWidgetEvent,
  SchemaNode,
  XUX_FORM_ACTIONS,
  XUX_GRID_ACTIONS,
} from '@app/shared/types';
import { hasMethod } from '@app/shared/utils/common-utils';
import { UiActions } from '@app/store/actions/ui/ui.actions';
import { WIDGET_FAMILY } from '@config/global-constants';
import { asObservable } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { GridApi } from 'ag-grid-community';
import {
  chain,
  first,
  get,
  has,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  set,
  some,
} from 'lodash';
import { createFormGroupFromSchema } from '../services/schema-form/create-form';
import { createFormDefinitionsFromSchema } from '../services/schema-form/create-groups';
import { ValidationService } from '../services/validation/validation.service';
import { ICommonFormConfig, IFormExpanded } from './common-form.types';

@Component({
  selector: 'xvi-common-form',
  templateUrl: './common-form.component.html',
  styleUrls: ['./common-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommonFormComponent<D = IBaseEntityType>
  implements OnInit, OnChanges, OnDestroy
{
  public static INITIAL_TAB_INDEX: number = 0;

  @Input()
  public show: boolean;

  @Input()
  public document: D = {} as D;

  @Input()
  public form: FormGroup;

  @Input()
  public groups: IFormGroupPreferences[] = [];

  @Input()
  public defaultWidgetFamily: string = WIDGET_FAMILY.XUX;

  @Input()
  public dynamicWidgetsMap: Record<string, Type<any>>;

  @Input()
  public formConfig: ICommonFormConfig<D> = null;

  @Input() public expanded$: BehaviorSubject<IFormExpanded> =
    new BehaviorSubject<IFormExpanded>({
      key: '',
      expanded: false,
    });

  @Input() public disabledFunc: () => boolean;

  /**
   * This input is optional
   */
  @Input() public groupIndexControl: FormControl;

  @Output()
  public formValueChanged: EventEmitter<D> = new EventEmitter<D>();

  @Output()
  public onFormAction: EventEmitter<ICommonFormAction> =
    new EventEmitter<ICommonFormAction>();

  @Output()
  public onExpandChanged: EventEmitter<Record<string, boolean>> =
    new EventEmitter<Record<string, boolean>>();

  public expandedObs: Observable<Record<string, boolean>>;

  public showLoader: boolean;

  public destroy$: Subject<void> = new Subject<void>();

  public error$: Observable<XVIError> = observableOf(null);
  public schemaSubscription: Subscription;

  public setupEmitWidgetEventMethod: Function;

  public activeTabIndex: number = CommonFormComponent.INITIAL_TAB_INDEX;
  public activeTabName: string = '';

  public formAction: Subject<IWidgetAction> = new Subject<IWidgetAction>();

  protected form$: BehaviorSubject<FormGroup | undefined> = new BehaviorSubject(
    undefined,
  );

  public gridApi: GridApi = null;

  /** Notice: keep in private and use {@link emitWidgetEvent} method to dispatch events */
  protected _widgetsEventBus$: Subject<IWidgetEvent> =
    new Subject<IWidgetEvent>();
  protected formSubscriptions: Subscription[] = [];

  private tabIndexControlSub: Subscription;
  private lastEmittedTabIndex: number;

  public constructor(
    public uiActions: UiActions,
    public stateService: StateService,
    public entityFormServiceProvider: EntityFormServiceProvider,
    public i18nService: XVII18nService,
  ) {}

  public ngOnInit(): void {
    this.expandedObs = this.expanded$.pipe(
      rx.distinctUntilChanged(),
      rx.scan(
        (acc, cur) =>
          isEmpty(cur.key) ? { ...acc } : { ...acc, [cur.key]: cur.expanded },
        {},
      ),
      rx.shareReplay(1),
    );

    this.expandedObs
      .pipe(rx.distinctUntilChanged(), rx.takeUntil(this.destroy$))
      .subscribe((expanded) => {
        this.formAction.next({
          type: XUX_FORM_ACTIONS.FORM_EXPAND_CHANGED,
          payload: { expanded },
        });

        this.onExpandChanged.emit(expanded);
      });

    if (!this.groupIndexControl) {
      this.groupIndexControl = new FormControl(
        CommonFormComponent.INITIAL_TAB_INDEX,
      );
    }

    this.setupEmitWidgetEventMethod = get(
      this.formConfig,
      'setupEmitWidgetEvent',
      this.setupEmitWidgetEventMethod,
    );

    this.showLoader = get(this.formConfig, 'showLoader', false);

    this.updateSchemaSubscription(
      asObservable(
        get(this.formConfig, 'schemaObs', get(this.formConfig, 'schema')),
      ),
    );

    this.formSubscriptions.push(
      this.form$.subscribe((form) => {
        this.form = form;
      }),
    );
  }

  public ngOnChanges(
    changes: Record<keyof CommonFormComponent<D>, SimpleChange>,
  ): void {
    if (has(changes, 'document') && !isNil(this.form)) {
      this.form.patchValue(this.document);
    }

    if (has(changes, 'groups')) {
      if (!this.formAction) {
        this.initFormAction();
      }

      if (!this.groupIndexControl) {
        this.groupIndexControl = new FormControl(
          CommonFormComponent.INITIAL_TAB_INDEX,
        );
      }
    }
  }

  public ngOnDestroy(): void {
    this.formSubscriptions.forEach((sub) => sub.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
    this.schemaSubscription?.unsubscribe();
  }

  public expandRightContent(isCollapsed: boolean, formKey: string): void {
    this.expanded$.next({ key: formKey, expanded: !isCollapsed });
  }

  public isFormExpanded(formKey?: string): Observable<boolean> {
    return this.expandedObs.pipe(
      rx.map((expandedObj) => {
        return isEmpty(formKey)
          ? some(expandedObj, (value) => value)
          : get(expandedObj, [formKey], false);
      }),
    );
  }

  /**
   * returns true when provided index is equal to indexControl value
   */
  public isActiveTab(tabIndex: number): boolean {
    return tabIndex === get(this.groupIndexControl, 'value');
  }

  public setupEmitWidgetEvent(): void {
    if (!this.setupEmitWidgetEventMethod) return;

    setTimeout(
      () => this.setupEmitWidgetEventMethod(this.emitWidgetEvent.bind(this)),
      0,
    );
  }

  public beforeFormGroupChange = async (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    groupIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    group: IFormGroupPreferences,
  ): Promise<boolean> => {
    return true;
  };

  protected emitWidgetEvent<T extends AnyObject>(
    widgetFieldPath: string,
    eventName: string,
    data: T,
  ): void {
    this._widgetsEventBus$.next({ widgetFieldPath, eventName, data });
  }

  protected updateSchemaSubscription(schemaObs: Observable<SchemaNode>): void {
    if (this.schemaSubscription) {
      this.schemaSubscription.unsubscribe();
    }
    this.schemaSubscription = schemaObs
      .pipe(
        rx.map((schema = {}) => {
          // map schema before form initialization
          if (isFunction(this.formConfig?.schemaMapper)) {
            return this.formConfig.schemaMapper.call(this, schema);
          }

          return schema;
        }),
      )
      .subscribe((schema) => {
        if (!this.form) {
          this.initForm(schema);
        }
      });
  }

  protected initForm(schema: SchemaNode): void {
    const formRules = this.formConfig.formRules;
    const form = createFormGroupFromSchema({
      schema,
      rules: formRules,
      i18nService: this.i18nService,
    });
    // this.formService.resolveForm(form);
    this.form = form;
    this.form$.next(form);
    if (!this.formConfig.validation) {
      // TODO: turn off validation for deeply nested controls
      Object.values(this.form.controls || {}).forEach((control) =>
        control.clearValidators(),
      );
    } else {
      this.error$ = observableCombineLatest([
        this.form.statusChanges.pipe(rx.startWith(null)),
        this.form.valueChanges.pipe(rx.startWith(null)),
      ]).pipe(
        rx.throttleTime(250, undefined, { leading: true, trailing: true }),
        rx.map(() =>
          this.form.valid
            ? null
            : ValidationService.findFormError(this.form, formRules, {
                i18nService: this.i18nService,
              }),
        ),
        rx.distinctUntilChanged(isEqual),
      );
    }

    if (!isEmpty(this.document)) {
      this.form.patchValue(this.document);
    }

    const newGroups = createFormDefinitionsFromSchema({
      schema,
      rules: this.formConfig.formRules,
      form: this.form,
      eventBus$: this._widgetsEventBus$.asObservable(),
      i18nService: this.i18nService,
      keepReference: true,
    });

    const formDisabledObs = get(this.formConfig, 'disabledObs');

    if (!isNil(formDisabledObs) || !isNil(this.disabledFunc)) {
      // form can be disabled by either formConfig.disabledObs or disabledFunc, if both provided, formConfig.disabledObs will be used
      const formDisabledObs$ = isNil(formDisabledObs)
        ? observableOf(this.disabledFunc())
        : formDisabledObs;

      chain(newGroups)
        .map('fields')
        .flatten()
        .forEach((field) => {
          set(field, 'disabledObs', formDisabledObs$);
        })
        .value();
    }

    this.groups = newGroups;

    if (isEmpty(get(this.formConfig, ['formRules', 'groups']))) {
      this.groups.forEach((group) => (group.name = ''));
    }

    if (isFunction(this.formConfig.onFormReady)) {
      setTimeout(() => this.formConfig.onFormReady(this.form, this.groups), 0);
    }

    // init form action when groups is ready
    this.initFormAction();

    this.formAction.next({
      type: XUX_FORM_ACTIONS.FORM_READY,
      payload: { form: this.form, formKey: this.formConfig?.formKey },
    });

    this.formValueSubscription();
  }

  protected formValueSubscription(): void {
    this.form.valueChanges
      .pipe(rx.takeUntil(this.destroy$))
      .subscribe((value) => {
        this.formValueChanged.emit(value);
      });
  }

  /**
   * Createas a subscription for form actions to reemit actions as own events
   */
  private initFormAction(): void {
    this.formAction = get(first(this.groups), 'action');

    if (hasMethod(this.formAction, 'subscribe')) {
      this.formSubscriptions.push(
        this.formAction.subscribe((action) => {
          switch (action.type) {
            case XUX_GRID_ACTIONS.GRID_READY:
              this.gridApi = action.payload?.gridApi || null;
              break;
            case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
            case XUX_FORM_ACTIONS.FORM_EXPAND_CHANGED:
              // need to reset row heights to make sure each rows are sized correctly after form expand change
              // add need timeout to make sure grid has rendered before resetting row heights
              setTimeout(() => {
                this.gridApi?.resetRowHeights();
              }, 0);
              break;
            default:
              break;
          }

          this.onFormAction.emit({
            ...action,
            formKey: this.formConfig?.formKey,
            formValue: this.form.value,
            selectedGroupIndex: this.groupIndexControl.value,
          });
        }),
      );
    }
  }
}
